import { Check, Lock, LockOpen, MessageSquare, Minus, Paperclip, Pencil, Plus, Receipt, Trash2, Wrench, X } from "lucide-react";
import { useEffect, useState } from "react";
import { dateTime, money } from "../../format";
import { Card, Later } from "./Card";
import { useAttachmentOpener } from "./attachments";
import { useDraft } from "./estimateDraft";
import { textability } from "./RightColumn";
import { useTicketContext } from "./useTicket";

/**
 * The service checklist, watch by watch (owner, 2026-11-03): pick the watch, then tick its
 * services. Lines not tied to a watch (older tickets, return shipping) are under "Whole ticket".
 */
export function ServicesCard() {
  const { detail, lookups, save, saving } = useTicketContext();
  const { draft, setDraft, dirty } = useDraft();
  const [showAll, setShowAll] = useState(false);
  const watches = detail.watches;
  const [watchId, setWatchId] = useState<string | null>(watches[0]?.id ?? null);
  useEffect(() => { if (watchId && !watches.some((w) => w.id === watchId)) setWatchId(watches[0]?.id ?? null); }, [watches, watchId]);
  const locked = detail.ticket.servicesLocked;
  const services = lookups?.services ?? [];
  const lineOf = (id: string) => draft.lines.findIndex((l) => l.serviceId === id && (l.watchId ?? null) === watchId);
  const shown = services.filter((s) => showAll || s.isFavorite || lineOf(s.id) >= 0);

  const toggle = (id: string, on: boolean) => {
    const s = services.find((x) => x.id === id)!;
    setDraft({ ...draft, lines: on
      ? [...draft.lines, { serviceId: s.id, watchId, name: s.name, description: s.description, quantity: 1, price: s.price, discount: null }]
      : draft.lines.filter((l) => !(l.serviceId === id && (l.watchId ?? null) === watchId)) });
  };
  const setQty = (id: string, q: number) => {
    const i = lineOf(id);
    if (i < 0) { if (q > 0) toggle(id, true); return; }
    if (q < 1) { toggle(id, false); return; }
    setDraft({ ...draft, lines: draft.lines.map((l, j) => (j === i ? { ...l, quantity: q } : l)) });
  };
  const count = (wid: string | null) => draft.lines.filter((l) => (l.watchId ?? null) === wid && !/^return shipping/i.test(l.name)).length;

  return (
    <Card title="Services" icon={Wrench} className={locked ? "locked" : ""}>
      <div className="watch-tabs" role="tablist" aria-label="Watch">
        {watches.map((w) => (
          <button key={w.id} type="button" role="tab" aria-selected={watchId === w.id} className={watchId === w.id ? "on" : ""} onClick={() => setWatchId(w.id)}>
            Watch {w.position}{w.brand ? ` · ${w.brand}` : ""}{count(w.id) ? <span className="n">{count(w.id)}</span> : null}
          </button>
        ))}
        <button type="button" role="tab" aria-selected={watchId === null} className={watchId === null ? "on" : ""} onClick={() => setWatchId(null)}
          title="Services not tied to one watch">Whole ticket{count(null) ? <span className="n">{count(null)}</span> : null}</button>
        <button type="button" className="add" disabled={saving || dirty} title={dirty ? "Save the estimate first" : "Add a watch to this ticket"}
          onClick={() => void save("POST", "/watches", {})}><Plus size={14} />Watch</button>
      </div>
      {/* Several columns of services across the wide column (owner, 2026-11-03). */}
      <ul className="svc-grid">
        {shown.map((s) => {
          const i = lineOf(s.id);
          const q = i >= 0 ? draft.lines[i]!.quantity : 0;
          return (
            <li key={s.id} className={i >= 0 ? "on" : ""}>
              <input type="checkbox" checked={i >= 0} disabled={locked} aria-label={s.name} onChange={(e) => toggle(s.id, e.target.checked)} />
              <span className="svc-name" onClick={() => !locked && toggle(s.id, i < 0)}>{s.name}</span>
              <span className="stepper">
                <button type="button" disabled={locked || q < 1} aria-label={`Fewer ${s.name}`} onClick={() => setQty(s.id, q - 1)}><Minus size={14} /></button>
                <span>{i >= 0 ? q : 1}</span>
                <button type="button" disabled={locked} aria-label={`More ${s.name}`} onClick={() => setQty(s.id, (i >= 0 ? q : 0) + 1)}><Plus size={14} /></button>
              </span>
            </li>
          );
        })}
      </ul>
      <div className="svc-foot">
        <button type="button" className="link-btn" onClick={() => setShowAll(!showAll)}>{showAll ? "Show Favorites" : "Show All"}</button>
        <button type="button" className="link-btn" disabled={saving || dirty}
          title={dirty ? "Save or discard estimate changes first" : locked ? "Unlock to change services" : "Lock so services cannot be changed"}
          onClick={() => void save("PATCH", "", { servicesLocked: !locked, expectedUpdatedAt: detail.ticket.updatedAt })}>
          {locked ? <Lock size={15} /> : <LockOpen size={15} />}{locked ? "Locked" : "Unlocked"}
        </button>
      </div>
    </Card>
  );
}

export function ExpensesCard() {
  const { detail, lookups, save, saving } = useTicketContext();
  const empty = { supplier: "", category: "", description: "", amount: "" };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState<string | null>(null);
  const [edit, setEdit] = useState(empty);
  const total = detail.expenses.reduce((s, e) => s + Number(e.amount), 0);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm({ ...form, [k]: e.target.value });
  return (
    <Card title="Ticket Expenses" icon={Receipt} actions={<span className="strong">Total: {money(total)}</span>}>
      <table className="t-mini">
        <thead><tr><th>Expense</th><th>Description</th><th className="num">Amount ($)</th><th className="center">Actions</th></tr></thead>
        <tbody>
          {detail.expenses.map((e) => editing === e.id ? (
            <tr key={e.id} className="editing">
              <td><input value={edit.supplier} list="expense-suppliers" onChange={(x) => setEdit({ ...edit, supplier: x.target.value })} aria-label="Source" autoFocus /></td>
              <td><input value={edit.category} list="expense-categories" onChange={(x) => setEdit({ ...edit, category: x.target.value })} aria-label="Category" />
                <input value={edit.description} placeholder="Description (optional)" onChange={(x) => setEdit({ ...edit, description: x.target.value })} aria-label="Description" /></td>
              <td className="num"><input type="number" min={0} step="0.01" value={edit.amount} onChange={(x) => setEdit({ ...edit, amount: x.target.value })} aria-label="Amount" /></td>
              <td className="center nowrap">
                <button type="button" className="icon-link" aria-label="Save expense" disabled={saving}
                  onClick={async () => { if (await save("PUT", `/expenses/${e.id}`, edit)) setEditing(null); }}><Check size={16} /></button>
                <button type="button" className="icon-link" aria-label="Cancel" onClick={() => setEditing(null)}><X size={16} /></button>
              </td>
            </tr>
          ) : (
            <tr key={e.id}>
              <td className="caps">{e.supplier}</td>
              <td className="caps">{[e.category, e.description].filter(Boolean).join(" · ")}</td>
              <td className="num">{money(e.amount)}</td>
              <td className="center nowrap">
                <button type="button" className="icon-link" aria-label="Edit expense" disabled={saving}
                  onClick={() => { setEdit({ supplier: e.supplier ?? "", category: e.category ?? "", description: e.description ?? "", amount: e.amount }); setEditing(e.id); }}><Pencil size={15} /></button>
                <button type="button" className="icon-link danger" aria-label="Delete expense" disabled={saving}
                  onClick={() => { if (window.confirm(`Delete the ${money(e.amount)} expense?`)) void save("DELETE", `/expenses/${e.id}`); }}><Trash2 size={16} /></button>
              </td>
            </tr>
          ))}
          {!detail.expenses.length && <tr><td colSpan={4} className="hint">No expenses.</td></tr>}
        </tbody>
      </table>
      <form className="exp-add" onSubmit={async (e) => { e.preventDefault(); if (await save("POST", "/expenses", form)) setForm(empty); }}>
        <input list="expense-suppliers" placeholder="Source" value={form.supplier} onChange={set("supplier")} aria-label="Source" />
        <datalist id="expense-suppliers">{lookups?.expenseSuppliers.map((v) => <option key={v} value={v} />)}</datalist>
        <input list="expense-categories" placeholder="Category" value={form.category} onChange={set("category")} aria-label="Category" />
        <datalist id="expense-categories">{lookups?.expenseCategories.map((v) => <option key={v} value={v} />)}</datalist>
        <input type="number" min={0} step="0.01" placeholder="Amount ($)" required value={form.amount} onChange={set("amount")} aria-label="Amount" />
        <button type="submit" className="btn primary" disabled={saving || !form.amount || (!form.supplier && !form.category)}>Add</button>
      </form>
    </Card>
  );
}

/** Touches: texts with the customer (and later calls and voicemails). */
const STATUS_LABEL: Record<string, string> = {
  scheduled: "waiting for 8 AM", pending: "sending", queued: "sent to Twilio", accepted: "sent to Twilio", sending: "sending",
  sent: "sent", delivered: "delivered", undelivered: "not delivered", failed: "failed", unknown: "status unknown", received: "", cancelled: "not sent (no longer needed)",
};

/** Texts with the customer: what staff and the system sent, and customer replies (unread until cleared). */
export function TouchesCard() {
  const { detail, save, saving } = useTicketContext();
  const [draft, setDraft] = useState("");
  const { open, failed } = useAttachmentOpener();
  const { canText, whyNoText } = textability(detail.customer);
  const name = [detail.customer?.firstName, detail.customer?.lastName].filter(Boolean).join(" ") || "Customer";
  return (
    <Card title="Touches" icon={MessageSquare}>
      <form className="compose" onSubmit={async (e) => {
        e.preventDefault();
        if (await save("POST", "/texts", { body: draft })) setDraft("");
      }}>
        <textarea rows={3} placeholder={canText ? "Enter a text message to the customer..." : `${whyNoText}.`} aria-label="New text message"
          value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={1500} disabled={!canText} />
        <button type="submit" className="btn primary send" disabled={!canText || saving || !draft.trim()}
          title={canText ? `Texts ${detail.customer?.phone}. Until go-live, texts use Twilio's test mode and are not delivered.` : whyNoText}>
          {saving ? "Sending…" : "Send"}</button>
      </form>
      <ul className="thread">
        {detail.texts.map((m) => {
          const status = m.deliveryStatus === "scheduled" && m.sendAfter ? `waiting until ${dateTime(m.sendAfter)}` : STATUS_LABEL[m.deliveryStatus ?? ""] ?? m.deliveryStatus;
          return (
            <li key={m.id} className={`${m.fromCustomer ? "in" : "out"}${m.unread ? " unread" : ""}`}>
              <div className="meta"><span className="who">{m.fromCustomer ? name : m.sentBy ?? "WRP (System)"}</span>
                <span>•</span><span>{dateTime(m.sendAfter ?? m.createdAt)}</span>
                {status && <span className={m.deliveryStatus === "failed" || m.deliveryStatus === "undelivered" ? "hint error" : "hint"}>· {status}{m.errorCode ? ` (Twilio ${m.errorCode})` : ""}</span>}</div>
              {m.body && <div className="body">{m.body}</div>}
              {m.media.length > 0 && (
                <ul className="attachments">
                  {m.media.map((p, i) => (
                    <li key={p.id}><button type="button" className="link" onClick={() => open(p.id)}>
                      <Paperclip size={14} aria-hidden /> {p.contentType.startsWith("image/") ? `Picture ${i + 1}` : `File ${i + 1}`}</button></li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
        {!detail.texts.length && <li className="hint plain">No texts yet.</li>}
      </ul>
      {failed && <p className="hint error">Could not open the picture: {failed}</p>}
    </Card>
  );
}
